<template>
  <div class="pagination-body">
    <div class="pagination-center">
      <div class="show-label">
        <span class="sl">{{ $t('pagination.labels.showing') }}</span>
        {{ recordCount ? offset * recordsPerPage + 1 : 0 }} - {{ pageLimit }}
        {{ $t('pagination.labels.of') }}
        {{ recordCount }}
      </div>

      <div class="pagination-controls">
        <div class="control left" :class="{ enabled: isLeftControlEnabled }">
          <div class="arrow" data-label="first" @click="goToPage(1)">
            <font-awesome-icon icon="angle-double-left" />
          </div>

          <div class="arrow" @click="previousPage">
            <font-awesome-icon icon="chevron-left" />
          </div>
        </div>
        <div class="page-indicator-holder">
          <div v-if="pagesToShow[0] > 1" aria-label="leading">...</div>
          <div
            v-for="(pageNum, index) in pagesToShow"
            :key="index"
            :class="{ active: isCurrentPage(pageNum) }"
            @click="goToPage(pageNum)"
          >
            {{ pageNum }}
          </div>
          <div v-if="pagesToShow[pagesToShow.length - 1] < pageCount" aria-label="trailing">
            ...
          </div>
        </div>
        <div class="control right" :class="{ enabled: isRightControlEnabled }">
          <div class="arrow" @click="nextPage">
            <font-awesome-icon icon="chevron-right" />
          </div>
          <div class="arrow" data-label="last" @click="goToPage(pageCount)">
            <font-awesome-icon icon="angle-double-right" />
          </div>
        </div>
      </div>
      <div class="page-size">
        <DropdownSelect
          :menu-items="pageSizeOptions"
          :selected-item="recordsPerPage"
          :show-close-on-selected="false"
          @select-item="onPageSizeOptionChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DropdownSelect from '@/app/components/DropdownSelect';

export default {
  name: 'Pagination',
  components: {
    DropdownSelect
  },
  props: {
    recordsPerPage: {
      type: Number,
      required: true
    },
    offset: {
      type: Number,
      required: true
    },
    recordCount: {
      type: Number,
      required: true
    },
    pageSizeOptions: {
      type: Array,
      default() {
        return [10, 25, 50, 100];
      }
    }
  },
  data() {
    return {
      isLeftControlEnabled: false,
      isRightControlEnabled: true,
      currentPage: this.offset + 1,
      windowSize: 7
    };
  },
  computed: {
    pageCount: {
      get() {
        return Math.ceil(this.recordCount / this.recordsPerPage);
      }
    },
    pageLimit: {
      get() {
        return this.recordCount - this.offset * this.recordsPerPage < this.recordsPerPage
          ? this.recordCount
          : this.offset * this.recordsPerPage + this.recordsPerPage;
      }
    },
    pagesToShow() {
      const pages = Array.from(Array(this.pageCount).keys()).map(item => item + 1);
      const extra = Math.floor(this.windowSize / 2);
      let start = this.currentPage - extra - 1;
      let stop = this.currentPage + extra;

      if (start < 0) {
        stop += 0 - start;
        start = 0;
      }

      if (stop > this.pageCount) {
        start -= stop - this.pageCount;
        stop = this.pageCount;
      }
      return pages.slice(Math.max(start, 0), Math.min(stop, this.pageCount));
    }
  },
  watch: {
    pageCount() {
      this.currentPage = this.offset + 1;
      this.togglePageControls();
    },
    currentPage() {
      this.togglePageControls();
    },
    offset() {
      this.currentPage = this.offset + 1;
    }
  },
  mounted() {
    this.togglePageControls();
  },
  methods: {
    togglePageControls() {
      this.isLeftControlEnabled = this.currentPage !== 1 && this.recordCount > 0;
      this.isRightControlEnabled = this.currentPage !== this.pageCount && this.recordCount > 0;
    },
    previousPage() {
      if (!this.isLeftControlEnabled) return;

      this.currentPage -= 1;
      this.emitParams();
    },
    nextPage() {
      if (!this.isRightControlEnabled) return;
      this.currentPage += 1;
      this.emitParams();
    },
    isCurrentPage(page) {
      return page === this.currentPage;
    },
    emitParams() {
      this.$emit('change', {
        recordsPerPage: this.recordsPerPage,
        offset: this.currentPage - 1
      });
    },
    goToPage(page) {
      if (this.recordCount === 0) return;
      this.currentPage = page;
      this.$emit('change', {
        recordsPerPage: this.recordsPerPage,
        offset: this.currentPage - 1
      });
    },
    onPageSizeOptionChange(dropdownItem) {
      this.$emit('change', { recordsPerPage: +dropdownItem, offset: 0 });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/app/styles/colors.scss';
@import '@/app/styles/mobile/mobile-pagination.scss';
/*
.pagination-body {
  display: flex;
  margin: 0 auto;
  padding: 60px;
  background-color: $white;

  border-top: 1px solid $light-grey-1;

  .pagination-center {
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;

    .page-size {
      margin-left: 10px;
    }
  }

  .pagination-controls {
    display: flex;
    flex-basis: 50%;
    justify-content: space-between;
    align-items: center;
    .control {
      display: inline-flex;

      text-align: center;
      background-color: $light-grey-2;
      &.right,
      &.left {
        font-size: 1.5rem;
        color: $light-grey-1;
        .arrow {
          display: inline-flex;
          position: relative;
          width: 35px;
          height: 35px;
          &:not(:first-of-type) {
            border-left: 2px solid $white;
          }
        }
        svg {
          margin: auto;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        &.enabled {
          background-color: $primary-blue;
          color: $white;
        }
        &:hover {
          cursor: pointer;
        }
      }
    }

    .page-indicator-holder {
      display: inline-flex;
      div {
        margin: 0 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        &.active,
        &:hover {
          cursor: pointer;
          background-color: $light-blue;
        }
      }
    }

    .record-count {
      flex-grow: 1;
    }
  }

  &.small {
    font-size: 0.8rem;
    padding: 30px;

    .pagination-center {
      .show-label .sl {
        display: none;
      }
    }
    .pagination-controls {
      .control {
        height: 30px;
      }
    }

    .page-indicator-holder {
      div {
        display: flex;
        justify-content: center;
        width: 30px;
        height: 30px;
      }
    }
  }
}
*/
</style>
